import { useQueryClient } from '@tanstack/vue-query';
import { waitForTransactionReceipt } from '@wagmi/core';
import { type Config } from '@wagmi/vue';
import * as devalue from 'devalue';
import { acceptHMRUpdate, defineStore } from 'pinia';
import type { Hash } from 'viem';

// import { useAlertsStore } from '@/stores/alerts/useAlertsStore';
import { useEventModalsStore } from '@/stores/eventModals/useEventModalsStore';
import { CartStateEnum, useCartStore } from '@/stores/cart/useCartStore';
import { useNuxtApp } from '#app';
import { useUserStore } from '~/stores/user/useUserStore';

export const useTransactionStore = defineStore(
    'transactions',
    () => {
        const alertStore = useAlertsStore();

        const modalStore = useEventModalsStore();

        const cartStore = useCartStore();

        const userStore = useUserStore();

        const transactions = ref<AppTransaction[]>([]);

        const pendingTransactions = computed(() => transactions.value.filter(item => item.status === 'pending'));

        const queryClient = useQueryClient();

        const wagmiConfig = ref<Nullable<Config>>(null);

        const { QueryWTFApiKey } = useWTFApi();

        const { $provider } = useNuxtApp();

        if (import.meta.client) {
            wagmiConfig.value = $provider.config;
        }

        const removeTransaction = (hash: Hash) => {
            transactions.value = transactions.value.filter(item => item.hash !== hash);
        };

        // const addTransaction = (transaction: AppTransaction) => {
        //     console.log('addTransaction', transaction);
        //     transactions.value = [
        //         ...transactions.value,
        //         transaction
        //     ];
        // };
        const getDescriptionForModal = (transaction: AppTransaction) => {
            console.log('getDescriptionForModal transaction: ', transaction);
            const firstItem = transaction.tokenList[0];
            if (!firstItem) return [];

            const totalPrice = transaction.tokenList.reduce((sum, item) => sum + (item.price?.amount || 0), 0);
            const currencySymbol = transaction.currency || firstItem.price?.symbol || '';

            if (isEvmErc721(firstItem.itemType)) {
                return [`${totalPrice} ${currencySymbol}`, `Chain: ${firstItem.network}`];
            }
            else if (isEvmErc1155(firstItem.itemType)) {
                const totalItems = transaction.tokenList.reduce((sum, item) => sum + (item.amount1155 || 1), 0);
                return [`${totalItems} item${totalItems > 1 ? 's' : ''} for ${totalPrice} ${currencySymbol}`, `Chain: ${firstItem.network}`];
            }

            return [];
        };

        const addTransaction = (transaction: AppTransaction) => {
            transactions.value = [...transactions.value, transaction];

            // При добавлении транзакции сразу добавляем модалку в статусе pending
            const totalPrice = transaction.tokenList.reduce((sum, item) => sum + (item.price?.amount || 0), 0);

            const previewImages = transaction.tokenList
                .slice(0, 3)
                .map(item => item.displayImageURL)
                .filter((url): url is string => url !== undefined);

            if (transaction.typeTransaction === 'purchase' && transaction.type === 'buyNow') {
                console.log('purchase pending');
                modalStore.add({
                    hash: transaction.hash,
                    type: 'complete',
                    status: 'type.pending',
                    images: previewImages,
                    imageState: 'default',
                    title: '',
                    description: getDescriptionForModal(transaction),
                    heading: 'Your purchase is complete!',
                    itemType: 2,
                    content: [
                        {
                            Price: `${totalPrice} ${transaction.currency}`
                        },
                        // {
                        //     // FIXME: нужна реальная комиссия
                        //     Fee: 1 + ' ' + transaction.currency
                        // },
                        {
                            'Total paid': `${totalPrice} ${transaction.currency}`,
                            'isBold': true
                        }
                    ],
                    button: {
                        callback: () => {
                            navigateTo(`/profile/${userStore.user}`);
                            modalStore.removeByType('complete');
                        },
                        title: 'View purchase'
                    }

                });
            }
            else if (transaction.typeTransaction === 'offer') {
                console.log('modal offer pending');
            }
            else if (transaction.typeTransaction === 'mint') {
                console.log('modal mint pending');
            }
        };

        watch(
            [
                pendingTransactions,
                wagmiConfig
            ],
            async ([
                allPendingTransactions,
                config
            ]) => {
                if (allPendingTransactions.length && config) {
                    const transactionPromises = allPendingTransactions.map(async ({
                        hash,
                        chainId,
                        type
                    }) => {
                        try {
                            const transactionReceipt = await waitForTransactionReceipt(
                                config,
                                {
                                    chainId,
                                    hash
                                }
                            );

                            if (transactionReceipt.status === 'success') {
                                const transaction = allPendingTransactions.find(t => t.hash === hash);

                                if (transaction) {
                                    if (transaction.typeTransaction === 'purchase' && transaction.type === 'buyNow') {
                                        modalStore.updateStatus(hash, 'type.success');
                                    }
                                    else if (transaction.typeTransaction === 'purchase') {
                                        modalStore.removeByType('approve');
                                    }
                                }

                                alertStore.add({
                                    title: 'Congratulations!',
                                    description: `Transaction ${hash} success`,
                                    critical: false,
                                    status: 'type.success'
                                });
                                await queryClient.invalidateQueries({
                                    predicate: query => query.queryKey[0] === 'balance'
                                });
                            }
                            return {
                                status: transactionReceipt.status,
                                hash,
                                type
                            };
                        }
                        catch (error) {
                            // alertStore.add({
                            //     title: 'Error',
                            //     description: `Transactions ${hash} error`,
                            //     critical: false,
                            //     status: 'type.error'
                            // });

                            const transaction = allPendingTransactions.find(t => t.hash === hash);
                            if (transaction) {
                                if (transaction.typeTransaction !== 'offer') {
                                    modalStore.updateModal(hash, {
                                        status: 'type.error',
                                        type: 'error',
                                        heading: 'Error',
                                        content: 'Something went wrong, Try again later.',
                                        button: {}
                                    });
                                }
                            }

                            removeTransaction(hash);
                        }
                    });

                    const statuses = await Promise.all(transactionPromises).then(transactions => transactions.filter(item => !!item));
                    statuses.forEach((item) => {
                        if (item.type === 'buyNow') {
                            removeTransaction(item.hash);
                        }
                        else {
                            transactions.value = transactions.value.map(transaction => ({
                                ...transaction,
                                status: item.status
                            }));
                        }
                    });
                }
            },
            { immediate: true }
        );

        const clearTransactions = () => {
            transactions.value = [];
        };

        watch(
            transactions,
            async (allTransactions) => {
                const isSuccess = allTransactions.length && allTransactions.every(item => item.status === 'success');
                if (isSuccess) {
                    cartStore.setCartState({
                        state: CartStateEnum.SUCCESS,
                        visible: true
                    });
                    cartStore.clearCart();
                    await queryClient.invalidateQueries({
                        predicate: query => query.queryKey[0] === QueryWTFApiKey.NFT_BY_ADDRESS
                    });
                    await queryClient.invalidateQueries({
                        predicate: query => query.queryKey[0] === QueryWTFApiKey.NFT_LIST
                    });
                }
            }
        );

        return {
            addTransaction,
            transactions,
            clearTransactions
        } as const;
    },
    {
        persist: {
            storage: piniaPluginPersistedstate.localStorage(),
            serializer: {
                serialize: state => devalue.stringify(state),
                deserialize: state => devalue.parse(state)
            }
        }
    }
);

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(
        useTransactionStore,
        import.meta.hot
    ));
}
